import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedIcon from "@mui/icons-material/Verified";

import { StoryComment } from "../../types/storyMap";
import { CreateCommentInput, getRememberedCommentName } from "../../api/storyMapComments";
import { relativeTime, Thread, threadsForScene } from "./commentThreads";

const TEAL = "#00d4ad";
const MAX_BODY = 2000;

export interface SceneCommentsProps {
  sceneId: string;
  /** Every comment on the story; the component picks out this scene's. */
  comments: StoryComment[];
  /** Account name when signed in; null means the reader types a name. */
  viewerName: string | null;
  /** Members of the story's organisation moderate every thread. */
  isOrgMember: boolean;
  onCreate: (input: CreateCommentInput) => Promise<unknown>;
  onResolve: (id: string, resolved: boolean) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  busy?: boolean;
  error?: string | null;
  /**
   * `reader`: collapsible section at the foot of a scene card in the
   * published story. `builder`: always-open panel in the editor's narrative
   * column, where the author answers readers.
   */
  variant?: "reader" | "builder";
}

interface ComposerProps {
  viewerName: string | null;
  placeholder: string;
  submitLabel: string;
  busy: boolean;
  autoFocus?: boolean;
  onSubmit: (body: string, authorName?: string) => Promise<unknown>;
  onCancel?: () => void;
}

function Composer({
  viewerName,
  placeholder,
  submitLabel,
  busy,
  autoFocus,
  onSubmit,
  onCancel,
}: ComposerProps) {
  const [body, setBody] = useState("");
  const [name, setName] = useState(() => getRememberedCommentName());
  const anonymous = viewerName === null;
  const canPost = body.trim().length > 0 && (!anonymous || name.trim().length > 0);

  const submit = async () => {
    if (!canPost) return;
    await onSubmit(body.trim(), anonymous ? name.trim() : undefined);
    setBody("");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {anonymous && (
        <TextField
          size="small"
          label="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={{ maxWidth: 260 }}
        />
      )}
      <TextField
        size="small"
        multiline
        minRows={2}
        maxRows={8}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={!canPost || busy}
          onClick={() => void submit()}
          sx={{
            textTransform: "none",
            bgcolor: TEAL,
            color: "#212b37",
            fontWeight: 600,
            "&:hover": { bgcolor: "#00bd9a" },
          }}
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button size="small" onClick={onCancel} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        )}
        {!anonymous && (
          <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
            Posting as {viewerName}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function CommentRow({
  comment,
  isReply,
  onDelete,
  busy,
}: {
  comment: StoryComment;
  isReply: boolean;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography variant={isReply ? "body2" : "subtitle2"} sx={{ fontWeight: 700 }}>
            {comment.author_name}
          </Typography>
          {comment.is_org_member && (
            <Tooltip title="From the organisation that published this story" arrow>
              <VerifiedIcon sx={{ fontSize: 15, color: TEAL }} />
            </Tooltip>
          )}
          {comment.mine && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              (you)
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {relativeTime(comment.created_at)}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 0.25 }}
        >
          {comment.body}
        </Typography>
      </Box>
      {comment.can_moderate && (
        <Tooltip title={isReply ? "Delete reply" : "Delete comment and its replies"} arrow>
          <span>
            <IconButton size="small" onClick={onDelete} disabled={busy} sx={{ mt: -0.5 }}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

function ThreadView({
  thread,
  viewerName,
  busy,
  onCreate,
  onResolve,
  onDelete,
}: {
  thread: Thread;
  viewerName: string | null;
  busy: boolean;
  onCreate: SceneCommentsProps["onCreate"];
  onResolve: SceneCommentsProps["onResolve"];
  onDelete: SceneCommentsProps["onDelete"];
}) {
  const [replying, setReplying] = useState(false);
  const { opener, replies } = thread;
  return (
    <Box
      sx={{
        borderLeft: `3px solid ${opener.resolved ? "rgba(0,0,0,0.12)" : TEAL}`,
        pl: 1.5,
        py: 0.5,
        opacity: opener.resolved ? 0.7 : 1,
      }}
    >
      <CommentRow
        comment={opener}
        isReply={false}
        busy={busy}
        onDelete={() => void onDelete(opener.id)}
      />
      {replies.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, ml: 2 }}>
          {replies.map((reply) => (
            <CommentRow
              key={reply.id}
              comment={reply}
              isReply
              busy={busy}
              onDelete={() => void onDelete(reply.id)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        {!replying && !opener.resolved && (
          <Button
            size="small"
            onClick={() => setReplying(true)}
            sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}
          >
            Reply
          </Button>
        )}
        {opener.can_moderate && (
          <Button
            size="small"
            disabled={busy}
            startIcon={
              opener.resolved ? (
                <ReplayIcon sx={{ fontSize: "16px !important" }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: "16px !important" }} />
              )
            }
            onClick={() => void onResolve(opener.id, !opener.resolved)}
            sx={{ textTransform: "none", minWidth: 0, px: 0.75, color: "text.secondary" }}
          >
            {opener.resolved ? "Reopen" : "Resolve"}
          </Button>
        )}
        {opener.resolved && opener.resolved_at && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Resolved {relativeTime(opener.resolved_at)}
          </Typography>
        )}
      </Box>
      {replying && (
        <Box sx={{ mt: 1, ml: 2 }}>
          <Composer
            viewerName={viewerName}
            placeholder={`Reply to ${opener.author_name}`}
            submitLabel="Reply"
            busy={busy}
            autoFocus
            onCancel={() => setReplying(false)}
            onSubmit={async (body, authorName) => {
              await onCreate({ body, parentId: opener.id, authorName });
              setReplying(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * Threaded comments on one scene. Readers open threads and reply; the
 * organisation (and a thread's own author) resolve, reopen and delete.
 */
export default function SceneComments({
  sceneId,
  comments,
  viewerName,
  isOrgMember,
  onCreate,
  onResolve,
  onDelete,
  busy = false,
  error = null,
  variant = "reader",
}: SceneCommentsProps) {
  const threads = useMemo(() => threadsForScene(comments, sceneId), [comments, sceneId]);
  const open = threads.filter((t) => !t.opener.resolved);
  const resolved = threads.filter((t) => t.opener.resolved);
  const isBuilder = variant === "builder";
  const [expanded, setExpanded] = useState(isBuilder);
  const [showResolved, setShowResolved] = useState(false);

  const countLabel =
    open.length === 0
      ? threads.length === 0
        ? "No comments yet"
        : "All comments resolved"
      : `${open.length} open comment${open.length === 1 ? "" : "s"}`;

  return (
    <Box
      className="no-print"
      sx={{
        mt: isBuilder ? 0 : 2.5,
        pt: isBuilder ? 0 : 1.5,
        borderTop: isBuilder ? "none" : "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Box
        role={isBuilder ? undefined : "button"}
        onClick={isBuilder ? undefined : () => setExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: isBuilder ? "default" : "pointer",
          userSelect: "none",
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {isBuilder ? "Reader comments" : "Comments"}
        </Typography>
        <Chip
          size="small"
          label={countLabel}
          sx={{
            height: 20,
            fontSize: 11,
            bgcolor: open.length > 0 ? "rgba(0,212,173,0.15)" : "rgba(0,0,0,0.06)",
          }}
        />
        {!isBuilder && (
          <IconButton size="small" sx={{ ml: "auto" }} aria-label={expanded ? "Hide comments" : "Show comments"}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ py: 0 }}>
              {error}
            </Alert>
          )}
          {open.map((thread) => (
            <ThreadView
              key={thread.opener.id}
              thread={thread}
              viewerName={viewerName}
              busy={busy}
              onCreate={onCreate}
              onResolve={onResolve}
              onDelete={onDelete}
            />
          ))}
          {resolved.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setShowResolved((v) => !v)}
                sx={{ textTransform: "none", alignSelf: "flex-start", color: "text.secondary" }}
              >
                {showResolved ? "Hide" : "Show"} {resolved.length} resolved
              </Button>
              <Collapse in={showResolved}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {resolved.map((thread) => (
                    <ThreadView
                      key={thread.opener.id}
                      thread={thread}
                      viewerName={viewerName}
                      busy={busy}
                      onCreate={onCreate}
                      onResolve={onResolve}
                      onDelete={onDelete}
                    />
                  ))}
                </Box>
              </Collapse>
            </>
          )}
          <Composer
            viewerName={viewerName}
            placeholder={
              isBuilder
                ? "Add a note to this scene's comments"
                : isOrgMember
                ? "Reply to readers, or leave a note on this scene"
                : "Ask a question or leave a comment on this scene"
            }
            submitLabel="Post comment"
            busy={busy}
            onSubmit={(body, authorName) => onCreate({ body, sceneId, authorName })}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
